import type { ComponentType, ReactNode } from "react";

// ============================================================================
// Photo Transform Types
// ============================================================================

export interface PhotoCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PhotoCropPosition {
  x: number;
  y: number;
}

export interface PhotoTransform {
  crop: PhotoCrop;
  zoom: number;
  cropPosition: PhotoCropPosition;
}

export interface PhotoWithTransform {
  url: string;
  transform: PhotoTransform | null;
}

/** A photo can be a simple URL string or an object with URL and transform data */
export type PhotoValue = string | PhotoWithTransform;

/** Extract URL from a PhotoValue (handles both string and object forms) */
export function getPhotoUrl(photo: PhotoValue): string {
  return typeof photo === "string" ? photo : photo.url;
}

// ============================================================================
// Page & Layout Types
// ============================================================================

export interface PageOptions {
  layout?: string;
  photos?: Record<number, PhotoValue>;
  labels?: Record<number, string>;
  dates?: Record<number, string>;
}

export interface Layout {
  value: string;
  label: string;
  countPhotos: number;
  countLabels: number;
  style: Record<string, string>;
  photoAspectRatios?: number[];
  photoCropShapes?: Array<"rect" | "round">;
  labelMaxLength?: number;
}

export interface Theme {
  value: string;
  label: string;
  src: string;
  mask: string;
  color: string;
  top?: string;
  hidden?: boolean;
}

// ============================================================================
// Product Options Types
// ============================================================================

export interface PhotoBookOptions {
  countPages: number;
  pages: Record<number, PageOptions>;
  coverPhoto: PhotoValue | null;
  theme: string | null;
  color: string | null;
  title: string | null;
}

export interface CalendarOptions {
  pages: Record<number, PageOptions>;
  coverPhoto: PhotoValue | null;
  theme: string | null;
  color: string | null;
  year: number;
}

export type ProductOptions = PhotoBookOptions | CalendarOptions;

// ============================================================================
// Shipping Types
// ============================================================================

export interface ShippingInfo {
  name: string;
  email: string;
  phone: string;
  address: string;
  wilaya: string;
  instructions?: string;
}

// ============================================================================
// Asset Types
// ============================================================================

export interface Asset {
  title: string;
  url: string;
  filename: string;
}

export interface PhotoRecommendation {
  min: number;
  recommended: number;
  message: string;
}

// ============================================================================
// Product Labels
// ============================================================================

export interface ProductLabels {
  pendingTitle: string;
  pendingMessage: string;
  newButton: string;
}

// ============================================================================
// Option Configuration
// ============================================================================

export interface OptionConfig<T = unknown> {
  defaultValue: T;
}

// ============================================================================
// Component Props
// ============================================================================

export interface ProductSettingsProps<T extends ProductOptions> {
  productId: string;
  userId: string;
  options: T;
  setOptions: (options: Partial<T>) => void;
}

export interface ProductEditProps<T extends ProductOptions> {
  productId: string;
  userId: string;
  options: T;
  setOptions: (options: Partial<T>) => void;
}

export interface ProductPreviewProps<T extends ProductOptions> {
  options: T;
}

// ============================================================================
// Product Type Configuration
// ============================================================================

export interface ProductTypeConfig<T extends ProductOptions> {
  name: string;
  title: string;
  labels: ProductLabels;
  options: Record<string, OptionConfig>;
  price: (options: T, code?: string, shippingPrice?: number) => number;
  priceRows: (options: T, code?: string, shippingPrice?: number) => Array<[string, string]>;
  isReady: (options: T) => boolean;
  photoRecommendation?: (options: T) => PhotoRecommendation;
  getAssets: (options: T) => Asset[];
  settings: ComponentType<ProductSettingsProps<T>>;
  edit: ComponentType<ProductEditProps<T>>;
  preview: ComponentType<ProductPreviewProps<T>>;
  thumbnail: (options: T) => ReactNode;
}

// ============================================================================
// Product Status
// ============================================================================

export type ProductStatus = "draft" | "order" | "shipped" | "archived";
